import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyPageDashboard } from '../hooks/useMyPageDashboard';
import { useContent } from '../content';
import { getPremiumAnalysis, PREMIUM_TYPES } from '../data/premiumTypes';
import { calculateBeyondTestResults } from '../utils/beyondTestLogic';
import { generateDynamicReport } from '../utils/innerAnalysisEngine';
import { getInnerUnifiedReport } from '../client';

const LOADING_HINTS = [
    'AI가 핵심 패턴을 읽는 중입니다',
    '감정/거리/행동 축을 정리하는 중입니다',
    '연애 관점으로 문장을 다듬는 중입니다',
    '근거와 해석을 연결하는 중입니다',
];

const DEFAULT_AI_SECTION_TITLES = {
    1: '기본 작동 방식',
    2: '열망하는 관계',
    3: '욕구와 방어 사이',
    4: '반복되는 패턴',
    5: '심층 통찰',
    6: '구조적 분석',
    7: '실행 가이드',
    8: '핵심 결론',
};

const DEFAULT_AI_SECTION_HINTS = {
    1: '지금 내가 어떻게 반응하는지',
    2: '내가 관계에서 진짜 원하는 것',
    3: '원하지만 망설이는 이유',
    4: '같은 문제가 반복되는 흐름',
    5: '겉으로 안 보이는 핵심 원인',
    6: 'S/E/B 축에서의 구조적 위치',
    7: '바로 실행할 행동 포인트',
    8: '이번 분석의 한 줄 요약',
};

const normalizeText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map((v) => normalizeText(v)).filter(Boolean).join('\n');
    if (typeof value === 'object') {
        const preferred = ['text', 'content', 'body', 'desc', 'description', 'summary', 'analysis', 'message'];
        for (const k of preferred) {
            if (value[k]) {
                const t = normalizeText(value[k]);
                if (t) return t;
            }
        }
        return Object.values(value).map((v) => normalizeText(v)).filter(Boolean).join('\n');
    }
    return String(value);
};

const prettifyKey = (key) => {
    const raw = String(key || '').trim();
    const lower = raw.toLowerCase();

    if (/mega[_\s-]*section/.test(lower)) return '현재 패턴';
    if (/beyond[_\s-]*section/.test(lower)) return '원하는 관계 방향';
    if (/core|summary|overview/.test(lower)) return '핵심 요약';
    if (/loop|pattern/.test(lower)) return '반복 패턴';
    if (/action|guide|plan|solution/.test(lower)) return '실행 가이드';
    if (/insight|deep/.test(lower)) return '심층 통찰';

    const normalized = raw
        .replace(/^section_?/i, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return normalized || '분석 항목';
};

const normalizeAiSections = (report) => {
    if (!report || typeof report !== 'object') return [];

    const sections = [];

    for (let i = 1; i <= 8; i += 1) {
        const text = normalizeText(report[`section${i}`] || report[`section_${i}`]);
        if (text) sections.push({ title: `${i}. ${DEFAULT_AI_SECTION_TITLES[i] || '분석'}`, text, accent: i === 8 });
    }

    if (sections.length > 0) return sections;

    Object.entries(report)
        .filter(([k]) => !k.startsWith('_'))
        .forEach(([k, v], idx) => {
            const text = normalizeText(v);
            if (!text) return;
            sections.push({
                title: `${idx + 1}. ${prettifyKey(k)}`,
                text,
                accent: false,
            });
        });

    return sections;
};

const condenseText = (text, maxSentences = 3) => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ').trim();
    const sentences = cleaned
        .split(/(?<=[.!?。！？])\s+/)
        .map((s) => s.trim())
        .filter(Boolean);
    if (sentences.length <= maxSentences) return cleaned;
    return `${sentences.slice(0, maxSentences).join(' ')} ...`;
};

const stripSectionNumber = (title) => String(title || '').replace(/^\d+\.\s*/, '').trim();

const InsideReport = () => {
    console.log("InsideReport Component MOUNTED");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { getC, loaded: contentLoaded } = useContent(['inside', 'beyond']);
    const { loading, loggedIn, stats, guestData } = useMyPageDashboard();

    const [reportData, setReportData] = useState(null);
    const [reportMode, setReportMode] = useState('general'); // 'general', 'love'
    const [aiUnifiedReport, setAiUnifiedReport] = useState(null);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiLoadingTick, setAiLoadingTick] = useState(0);
    const [showCompact, setShowCompact] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [status, setStatus] = useState('loading'); // loading, ready, missing_tests, error
    const aiRequestedRef = useRef(false);  // AI 중복 요청 방지
    const megaForAiRef = useRef(null);     // AI에 넘길 mega 데이터 캐시
    const beyondForAiRef = useRef(null);   // AI에 넘길 beyond 데이터 캐시
    const aiReportCacheRef = useRef({ general: null, love: null }); // 모드별 AI 리포트 캐시
    const sectionRefs = useRef({});

    useEffect(() => {
        if (loading || !contentLoaded) return;

        if (!loggedIn && !guestData?.hasPending) {
            console.warn("InsideReport: Not logged in and no guest data found. Redirecting to login.");
            navigate('/login');
            return;
        }

        // 1. Get Test Data
        const combinedMega = stats?.mega || guestData?.localStats?.mega;
        const combinedBeyond = stats?.beyond || guestData?.localStats?.beyond;

        const megaPayload = combinedMega?.payload || combinedMega;
        const megaAnswersLocal = megaPayload?.answers;
        const beyondDataLocal = combinedBeyond?.payload || combinedBeyond;

        if (!megaAnswersLocal || !beyondDataLocal) {
            console.warn("InsideReport: Missing test data required for Integrated Analysis.");
            setStatus('missing_tests');
            return;
        }

        // 2. Derive Keys & Scores
        const premiumAnalysis = getPremiumAnalysis(megaAnswersLocal);
        const premiumTypeLocal = premiumAnalysis?.type || PREMIUM_TYPES[0];
        const premiumId = premiumTypeLocal?.id;

        // Beyond Ideal Type
        let beyondIdealLocal = "";
        let beyondScoresLocal = { S: "A", E: "A", B: "A" };

        const bS = beyondDataLocal.S || beyondDataLocal.summary?.S || beyondDataLocal.payload?.S;
        const bE = beyondDataLocal.E || beyondDataLocal.summary?.E || beyondDataLocal.payload?.E;
        const bB = beyondDataLocal.B || beyondDataLocal.summary?.B || beyondDataLocal.payload?.B;

        if (bS && bE && bB) {
            beyondIdealLocal = `${bS}${bE}${bB}`;
            beyondScoresLocal = { S: bS, E: bE, B: bB };
        } else if (beyondDataLocal.payload?.answers) {
            try {
                const calc = calculateBeyondTestResults(beyondDataLocal.payload.answers);
                beyondIdealLocal = `${calc.S}${calc.E}${calc.B}`;
                beyondScoresLocal = { S: calc.S, E: calc.E, B: calc.B };
            } catch (e) {
                console.error("InsideReport: Beyond calculation error", e);
                beyondIdealLocal = "AAA";
            }
        }

        // 3. Check for Static Content (Only for general mode)
        const contentKey = `inside.type_${premiumId}_${beyondIdealLocal}`;
        const specificData = getC(contentKey);
        const isSpecificDataValid = reportMode === 'general' && specificData && !JSON.stringify(specificData).includes("inside.type_");

        if (isSpecificDataValid) {
            console.log("InsideReport: Static Content Found");
            const mappedSeb = {};
            if (specificData.seb_alignment) {
                Object.entries(specificData.seb_alignment).forEach(([axis, val]) => {
                    mappedSeb[axis] = {
                        title: val.title,
                        expanded: {
                            currentState: val.gravity,
                            targetState: val.beyond,
                            evolutionStrategy: val.analysis,
                            deepInsight: val.analysis
                        }
                    };
                });
            }
            setReportData({
                ...specificData,
                seb_alignment: mappedSeb,
                patches: specificData.solution?.items || [],
                expertSummary: specificData.solution?.expert_summary || "",
                premiumType: premiumTypeLocal
            });
            setStatus('ready');
        } else {
            // 4. Dynamic Generation
            try {
                console.log("InsideReport: Dynamic Generation", { reportMode });

                // Reality Scores (Mega)
                const megaScoresRaw = megaPayload?.seb?.scores || megaPayload?.scores || { S: 50, E: 50, F: 50 };
                const megaScores = {
                    S: Number(megaScoresRaw.S || 50),
                    E: Number(megaScoresRaw.E || 50),
                    B: Number(megaScoresRaw.F || megaScoresRaw.B || 50)
                };

                // Ideal Types (Beyond)
                const bS_fallback = beyondScoresLocal?.S || 'A';
                const bE_fallback = beyondScoresLocal?.E || 'A';
                const bB_fallback = beyondScoresLocal?.B || 'A';

                const beyondS = getC(`beyond.beyond_test.results.S.${bS_fallback}`);
                const beyondE = getC(`beyond.beyond_test.results.E.${bE_fallback}`);
                const beyondB = getC(`beyond.beyond_test.results.B.${bB_fallback}`);

                const engineResult = generateDynamicReport(
                    { score_seb: megaScores, answers: megaAnswersLocal },
                    beyondScoresLocal,
                    {
                        S: beyondS?.title || 'Unknown',
                        E: beyondE?.title || 'Unknown',
                        B: beyondB?.title || 'Unknown'
                    },
                    premiumTypeLocal,
                    reportMode
                );

                if (!engineResult) throw new Error("Engine returned null");

                setReportData({
                    ...engineResult,
                    premiumType: premiumTypeLocal,
                    neuro_chemistry: {
                        rows: [
                            {
                                hormone: engineResult.details.S.hormone.name,
                                mission: engineResult.details.S.hormone.mission,
                                gravity: `현실 ${Math.round(megaScores.S)}점`,
                                beyond: `이상 ${Math.round(engineResult.details.S.idealScore)}점`,
                                desc: engineResult.details.S.hormone.desc
                            },
                            {
                                hormone: engineResult.details.E.hormone.name,
                                mission: engineResult.details.E.hormone.mission,
                                gravity: `현실 ${Math.round(megaScores.E)}점`,
                                beyond: `이상 ${Math.round(engineResult.details.E.idealScore)}점`,
                                desc: engineResult.details.E.hormone.desc
                            },
                            {
                                hormone: engineResult.details.B.hormone.name,
                                mission: engineResult.details.B.hormone.mission,
                                gravity: `현실 ${Math.round(megaScores.B)}점`,
                                beyond: `이상 ${Math.round(engineResult.details.B.idealScore)}점`,
                                desc: engineResult.details.B.hormone.desc
                            }
                        ]
                    },
                    seb_alignment: {
                        S: { title: engineResult.details.S.subtitle, expanded: engineResult.details.S.expanded },
                        E: { title: engineResult.details.E.subtitle, expanded: engineResult.details.E.expanded },
                        B: { title: engineResult.details.B.subtitle, expanded: engineResult.details.B.expanded }
                    },
                    modular_conflict: engineResult.twist
                });
                setStatus('ready');
            } catch (err) {
                console.error("InsideReport: Generation Error", err);
                setErrorMsg(`리포트 생성 중 오류가 발생했습니다: ${err.message}`);
                setStatus('error');
            }
        }

        // 5. 데이터만 캐시 — AI 호출은 별도 useEffect에서
        if (combinedMega && combinedBeyond) {
            megaForAiRef.current = combinedMega;
            beyondForAiRef.current = combinedBeyond;
        }
    }, [loading, contentLoaded, loggedIn, stats, guestData, getC, navigate]);

    // AI 보고서 요청 함수 (모드 변경 시에도 재사용)
    const fetchAiReport = useCallback(async (mode) => {
        const mega = megaForAiRef.current;
        const beyond = beyondForAiRef.current;
        if (!mega || !beyond) return;
        if (isAiLoading) return;

        // 이미 만든 리포트는 다시 요청하지 않고 즉시 사용
        const cached = aiReportCacheRef.current?.[mode];
        if (cached) {
            setAiUnifiedReport(cached);
            return;
        }

        setIsAiLoading(true);
        try {
            const res = await getInnerUnifiedReport({ mega, beyond, mode });
            if (res) {
                aiReportCacheRef.current[mode] = res;
                setAiUnifiedReport(res);
            }
        } catch (err) {
            console.error("AI Unified Report 요청 실패:", err);
            // 429 Rate limit — 사용자에게 알림
            if (err.message?.includes('429') || err.message?.includes('Too Many')) {
                const rateLimitPayload = { _rateLimit: true };
                aiReportCacheRef.current[mode] = rateLimitPayload;
                setAiUnifiedReport(rateLimitPayload);
            } else {
                const errorPayload = { _error: err.message || '내면 분석 요청에 실패했습니다.' };
                aiReportCacheRef.current[mode] = errorPayload;
                setAiUnifiedReport(errorPayload);
            }
        } finally {
            setIsAiLoading(false);
        }
    }, [isAiLoading]);

    useEffect(() => {
        if (!isAiLoading) {
            setAiLoadingTick(0);
            return;
        }
        const id = setInterval(() => setAiLoadingTick((v) => v + 1), 250);
        return () => clearInterval(id);
    }, [isAiLoading]);

    const aiLoadingSeconds = Math.floor(aiLoadingTick / 4);
    const aiLoadingHint = LOADING_HINTS[Math.floor(aiLoadingSeconds / 4) % LOADING_HINTS.length];
    const normalizedSections = useMemo(() => normalizeAiSections(aiUnifiedReport), [aiUnifiedReport]);
    const sAxisFormationSection = useMemo(() => {
        const explicit = normalizeText(
            aiUnifiedReport?.s_axis_formation ||
            aiUnifiedReport?.section0 ||
            reportData?.deep_report?.s_axis_formation
        );
        if (explicit) {
            return {
                title: 'S축이 이렇게 형성된 이유',
                text: explicit,
                accent: false,
            };
        }

        const sExpanded = reportData?.seb_alignment?.S?.expanded || {};
        const currentState = normalizeText(sExpanded.currentState);
        const deepInsight = normalizeText(sExpanded.deepInsight || sExpanded.evolutionStrategy);

        if (currentState || deepInsight) {
            return {
                title: 'S축이 이렇게 형성된 이유',
                text: [
                    currentState ? `현재 S축 상태: ${currentState}` : '',
                    deepInsight ? `형성 배경: ${deepInsight}` : '',
                ].filter(Boolean).join('\n'),
                accent: false,
            };
        }

        return {
            title: 'S축이 이렇게 형성된 이유',
            text: '이 칸에는 S축(안정)이 현재처럼 형성된 배경이 들어갑니다.',
            accent: false,
        };
    }, [aiUnifiedReport, reportData]);
    const mergedSections = useMemo(() => {
        const rest = normalizedSections
            .filter((sec) => stripSectionNumber(sec.title) !== 'S축이 이렇게 형성된 이유')
            .map((sec) => ({
                ...sec,
                title: stripSectionNumber(sec.title) || '분석',
            }));

        return [sAxisFormationSection, ...rest].map((sec, idx) => ({
            ...sec,
            title: `${idx + 1}. ${sec.title}`,
        }));
    }, [normalizedSections, sAxisFormationSection]);
    const sectionsWithMeta = useMemo(() => {
        return mergedSections.map((sec, idx) => ({
            ...sec,
            number: idx + 1,
            anchorId: `ai-section-${idx + 1}`,
        }));
    }, [mergedSections]);
    const visibleSections = useMemo(() => {
        if (!sectionsWithMeta.length) return [];
        if (!showCompact) return sectionsWithMeta;
        return sectionsWithMeta.slice(0, 3).map((s) => ({
            ...s,
            text: condenseText(s.text, 3),
        }));
    }, [sectionsWithMeta, showCompact]);
    const sectionOutline = useMemo(() => {
        return sectionsWithMeta.map((sec, idx) => {
            const match = String(sec.title || '').match(/^(\d+)\./);
            const n = match ? Number(match[1]) : (idx + 1);
            const titleOnly = String(sec.title || `${n}. 분석`).replace(/^\d+\.\s*/, '').trim();
            return {
                number: n,
                title: titleOnly || (DEFAULT_AI_SECTION_TITLES[n] || '분석'),
                hint: DEFAULT_AI_SECTION_HINTS[n] || '해당 번호에서 이 주제를 다룹니다.',
                anchorId: sec.anchorId,
            };
        });
    }, [sectionsWithMeta]);

    const scrollToSection = useCallback((anchorId) => {
        const node = sectionRefs.current[anchorId] || document.getElementById(anchorId);
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    // 최초 1회 AI 보고서 요청 (데이터 준비되면)
    useEffect(() => {
        if (status !== 'ready') return;
        if (aiRequestedRef.current) return; // 이미 요청했으면 스킵
        if (!megaForAiRef.current || !beyondForAiRef.current) return;
        aiRequestedRef.current = true;
        fetchAiReport(reportMode);
    }, [status]); // status가 'ready'가 될 때 한 번만

    // 모드 전환 시 캐시된 결과를 즉시 표시 (초기화 방지)
    useEffect(() => {
        const cached = aiReportCacheRef.current?.[reportMode];
        if (cached) {
            setAiUnifiedReport(cached);
        }
    }, [reportMode]);

    if (loading || !contentLoaded || status === 'loading') {
        return (
            <div style={styles.container}>
                <div style={{ color: '#fff', textAlign: 'center', marginTop: '5rem' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                    <h2 style={{ fontSize: '1.5rem', color: '#fff' }}>분석 리포트 생성 중...</h2>
                    <p style={{ color: '#94a3b8' }}>Status: {status} | Data: {contentLoaded ? 'Loaded' : 'Pending'}</p>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <h1 style={styles.title}>오류 발생</h1>
                    <div style={styles.card}>
                        <p style={styles.message}>데이터를 처리하는 중 문제가 발생했습니다.</p>
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                            상세 오류: {errorMsg || "알 수 없는 처리 오류"}
                        </p>
                        <button onClick={() => navigate('/mypage')} style={styles.button}>마이페이지로 돌아가기</button>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'missing_tests') {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <h1 style={styles.title}>내면 통합 분석 (Inner Analysis)</h1>
                    <div style={styles.errorCard}>
                        <p style={styles.message}>
                            통합 분석을 위해서는 <strong>메가 시너지 테스트</strong>와 <strong>비욘드 테스트</strong> 결과가 모두 필요합니다.
                        </p>
                        <div style={styles.buttonGroup}>
                            <button onClick={() => navigate('/mega-test')} style={styles.button}>메가 시너지 테스트 하러 가기</button>
                            <button onClick={() => navigate('/beyond-test')} style={styles.button}>비욘드 테스트 하러 가기</button>
                        </div>
                        <button onClick={() => navigate('/mypage')} style={styles.textButton}>마이페이지로 돌아가기</button>
                    </div>
                </div>
            </div>
        );
    }

    // Final fallback rendering guard to avoid Black Screen
    if (status === 'ready' && !reportData) {
        console.warn("InsideReport: Status ready but reportData missing. Showing loading...");
        return (
            <div style={styles.container}>
                <div style={styles.loadingBox}>
                    <div style={styles.spinner} />
                    <p style={{ marginTop: '1rem' }}>데이터를 최종 구성 중입니다...</p>
                </div>
            </div>
        );
    }

    if (status === 'ready' && reportData) {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <div style={styles.header}>
                        <h1 style={styles.title}>🛰️ [Anti-Gravity] 통합 분석 보고서</h1>
                        <button onClick={() => navigate('/mypage')} style={styles.backButton}>✕</button>
                    </div>

                    {/* Mode Toggle */}
                    <div style={styles.modeToggleContainer}>
                        <button
                            onClick={() => {
                                if (reportMode === 'general') return;
                                setReportMode('general');
                                const cached = aiReportCacheRef.current?.general;
                                if (cached) setAiUnifiedReport(cached);
                                else fetchAiReport('general');
                            }}
                            style={reportMode === 'general' ? styles.modeButtonActive : styles.modeButton}
                        >
                            일반 통찰
                        </button>
                        <button
                            onClick={() => {
                                if (reportMode === 'love') return;
                                setReportMode('love');
                                const cached = aiReportCacheRef.current?.love;
                                if (cached) setAiUnifiedReport(cached);
                                else fetchAiReport('love');
                            }}
                            style={reportMode === 'love' ? styles.modeButtonLoveActive : styles.modeButton}
                        >
                            연애 통찰
                        </button>
                        <button
                            onClick={() => setShowCompact((v) => !v)}
                            style={showCompact ? styles.modeButtonActive : styles.modeButton}
                        >
                            {showCompact ? '핵심만' : '전체'}
                        </button>
                    </div>

                    <div style={reportMode === 'love' ? styles.scrollContainerLove : styles.scrollContainer}>
                        <div style={styles.reportWrapper}>
                            {isAiLoading ? (
                                <div style={{ padding: '4rem', textAlign: 'center' }}>
                                    <div style={{ ...styles.spinner, transform: `rotate(${aiLoadingTick * 18}deg)` }} />
                                    <p style={{ marginTop: '1rem', color: '#f1f5f9', fontWeight: 600 }}>
                                        AI 전문가가 내면 분석을 작성 중입니다
                                    </p>
                                    <p style={{ marginTop: '0.45rem', color: '#94a3b8', fontSize: '0.92rem' }}>
                                        {aiLoadingHint}
                                    </p>
                                    <p style={{ marginTop: '0.35rem', color: '#64748b', fontSize: '0.82rem' }}>
                                        로딩 {aiLoadingSeconds}초 경과 · 화면이 멈춘 것이 아니라 분석 중입니다
                                    </p>
                                </div>
                            ) : aiUnifiedReport?._rateLimit ? (
                                <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏰</div>
                                    <p style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#f1f5f9' }}>AI 분석 요청이 잠시 한도에 도달했습니다.</p>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>1~2분 후 다시 시도해주세요.</p>
                                    <button
                                        onClick={() => { aiRequestedRef.current = false; fetchAiReport(reportMode); }}
                                        style={{ ...styles.button, marginTop: '1.5rem', padding: '0.7rem 1.5rem', fontSize: '0.9rem' }}
                                    >
                                        🔄 다시 시도하기
                                    </button>
                                </div>
                            ) : aiUnifiedReport?._error ? (
                                <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                                    <div style={{ fontSize: '2.2rem', marginBottom: '1rem' }}>⚠️</div>
                                    <p style={{ fontSize: '1rem', marginBottom: '0.45rem', color: '#f1f5f9' }}>내면 분석 본문을 생성하지 못했습니다.</p>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>오류: {aiUnifiedReport._error}</p>
                                    <button
                                        onClick={() => { aiRequestedRef.current = false; fetchAiReport(reportMode); }}
                                        style={{ ...styles.button, marginTop: '1.25rem', padding: '0.7rem 1.5rem', fontSize: '0.9rem' }}
                                    >
                                        🔄 다시 시도하기
                                    </button>
                                </div>
                            ) : visibleSections.length > 0 ? (
                                <div style={styles.aiReportContainer}>
                                    <div style={styles.outlineBox}>
                                        <div style={styles.outlineTitle}>번호별 설명 목차</div>
                                        <div style={styles.outlineGrid}>
                                            {sectionOutline.map((item, idx) => (
                                                <button
                                                    key={`${item.number}-${idx}`}
                                                    type="button"
                                                    onClick={() => scrollToSection(item.anchorId)}
                                                    style={styles.outlineItemButton}
                                                >
                                                    <div style={styles.outlineNumber}>{item.number}</div>
                                                    <div>
                                                        <div style={styles.outlineItemTitle}>{item.title}</div>
                                                        <div style={styles.outlineItemHint}>{item.hint}</div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {visibleSections.map((sec, idx) => (
                                        <div
                                            id={sec.anchorId}
                                            ref={(el) => { sectionRefs.current[sec.anchorId] = el; }}
                                            style={styles.aiSection}
                                            key={`${sec.anchorId}-${idx}`}
                                        >
                                            <div style={sec.accent ? styles.aiBadgeAccent : styles.aiBadge}>{sec.title}</div>
                                            <div style={sec.accent ? styles.aiTextAccent : styles.aiText}>{sec.text}</div>
                                        </div>
                                    ))}
                                    {showCompact && sectionsWithMeta.length > 3 && (
                                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>
                                            핵심 3개만 표시 중입니다. `전체`를 누르면 모든 섹션을 볼 수 있습니다.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
                                    <p style={{ color: '#f1f5f9', marginBottom: '0.45rem' }}>응답은 받았지만 본문 텍스트가 비어 있습니다.</p>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>분석 결과 형식을 자동 보정 중입니다. 다시 시도해 주세요.</p>
                                    <button
                                        onClick={() => { aiRequestedRef.current = false; fetchAiReport(reportMode); }}
                                        style={{ ...styles.button, marginTop: '1.4rem', padding: '0.7rem 1.5rem', fontSize: '0.9rem' }}
                                    >
                                        🔄 다시 시도하기
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.errorCard}>
                <p style={styles.message}>리포트 상태를 확인할 수 없습니다.</p>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Status: {status} | Data: {reportData ? 'Ready' : 'Missing'}</p>
                <button onClick={() => navigate('/mypage')} style={styles.button}>마이페이지로 돌아가기</button>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: '#e2e8f0',
        display: 'flex',
        justifyContent: 'center',
        padding: '2rem 1rem',
        fontFamily: "'Pretendard', sans-serif",
    },
    aiReportContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2.5rem',
        padding: '1rem',
    },
    aiSection: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        padding: '2rem',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        lineHeight: 1.8,
    },
    aiBadge: {
        display: 'inline-block',
        padding: '0.4rem 1rem',
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        color: '#60a5fa',
        borderRadius: '100px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        marginBottom: '1.2rem',
        border: '1px solid rgba(59, 130, 246, 0.2)',
    },
    aiBadgeAccent: {
        display: 'inline-block',
        padding: '0.4rem 1rem',
        backgroundColor: 'rgba(167, 139, 250, 0.15)',
        color: '#a78bfa',
        borderRadius: '100px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        marginBottom: '1.2rem',
        border: '1px solid rgba(167, 139, 250, 0.2)',
    },
    aiText: {
        fontSize: '1.1rem',
        color: '#cbd5e1',
        whiteSpace: 'pre-line',
    },
    aiTextAccent: {
        fontSize: '1.1rem',
        color: '#fff',
        fontWeight: '500',
        whiteSpace: 'pre-line',
    },
    outlineBox: {
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        border: '1px solid rgba(147, 197, 253, 0.28)',
        borderRadius: '16px',
        padding: '1rem',
        marginBottom: '0.25rem',
    },
    outlineTitle: {
        fontSize: '0.88rem',
        fontWeight: '800',
        color: '#bfdbfe',
        marginBottom: '0.65rem',
    },
    outlineGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '0.5rem',
    },
    outlineItem: {
        display: 'flex',
        gap: '0.55rem',
        alignItems: 'flex-start',
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        border: '1px solid rgba(148, 163, 184, 0.22)',
        borderRadius: '10px',
        padding: '0.55rem 0.6rem',
    },
    outlineItemButton: {
        display: 'flex',
        gap: '0.55rem',
        alignItems: 'flex-start',
        width: '100%',
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        border: '1px solid rgba(148, 163, 184, 0.22)',
        borderRadius: '10px',
        padding: '0.55rem 0.6rem',
        cursor: 'pointer',
        textAlign: 'left',
    },
    outlineNumber: {
        width: '20px',
        height: '20px',
        borderRadius: '999px',
        backgroundColor: 'rgba(59, 130, 246, 0.25)',
        color: '#dbeafe',
        fontSize: '0.75rem',
        fontWeight: '800',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        marginTop: '1px',
    },
    outlineItemTitle: {
        fontSize: '0.82rem',
        color: '#f8fafc',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    outlineItemHint: {
        fontSize: '0.76rem',
        color: '#94a3b8',
        lineHeight: 1.35,
        marginTop: '0.1rem',
    },
    content: {
        width: '100%',
        maxWidth: '1200px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    title: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#fff',
        margin: 0,
    },
    backButton: {
        background: 'transparent',
        border: 'none',
        color: '#94a3b8',
        fontSize: '1.5rem',
        cursor: 'pointer',
    },
    modeToggleContainer: {
        display: 'flex',
        gap: '0.5rem',
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
        padding: '0.4rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        width: 'fit-content',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    modeButton: {
        padding: '0.6rem 1.2rem',
        borderRadius: '8px',
        border: 'none',
        background: 'transparent',
        color: '#94a3b8',
        fontSize: '0.95rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    modeButtonActive: {
        padding: '0.6rem 1.2rem',
        borderRadius: '8px',
        border: 'none',
        background: '#3b82f6',
        color: '#fff',
        fontSize: '0.95rem',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    },
    modeButtonLoveActive: {
        padding: '0.6rem 1.2rem',
        borderRadius: '8px',
        border: 'none',
        background: 'linear-gradient(135deg, #f43f5e, #ec4899)',
        color: '#fff',
        fontSize: '0.95rem',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
    },
    scrollContainer: {
        backgroundColor: 'rgba(15, 23, 42, 0.3)',
        borderRadius: '24px',
        padding: '0.5rem',
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    scrollContainerLove: {
        backgroundColor: 'rgba(244, 63, 94, 0.03)',
        borderRadius: '24px',
        padding: '0.5rem',
        border: '1px solid rgba(244, 63, 94, 0.2)',
        boxShadow: '0 0 30px rgba(244, 63, 94, 0.05)',
    },
    reportWrapper: {
        padding: '2rem',
    },
    reportInnerHeader: {
        textAlign: 'center',
        marginBottom: '4rem',
    },
    headerLabel: {
        fontSize: '0.9rem',
        color: '#60a5fa',
        fontWeight: 'bold',
        letterSpacing: '2px',
        marginBottom: '1rem',
        display: 'block',
    },
    titleInner: {
        fontSize: '2.5rem',
        fontWeight: '900',
        background: 'linear-gradient(to right, #fff, #94a3b8)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '1.5rem',
    },
    titleLove: {
        fontSize: '2.5rem',
        fontWeight: '900',
        background: 'linear-gradient(to right, #f43f5e, #fb7185, #fff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '1.5rem',
        textShadow: '0 0 20px rgba(244, 63, 94, 0.2)',
    },
    subtitle: {
        fontSize: '1.1rem',
        lineHeight: 1.6,
        color: '#94a3b8',
        maxWidth: '700px',
        margin: '0 auto',
    },
    section: {
        marginBottom: '3.5rem',
    },
    sectionHeader: {
        fontSize: '1.2rem',
        color: '#94a3b8',
        borderBottom: '1px solid #334155',
        paddingBottom: '0.5rem',
        marginBottom: '2rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    card: {
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '20px',
        padding: '2.5rem',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    },
    profileTitle: {
        fontSize: '1.6rem',
        fontWeight: 'bold',
        color: '#fff',
        marginBottom: '1.5rem',
        lineHeight: 1.3,
    },
    catchphraseBox: {
        backgroundColor: 'rgba(96, 165, 250, 0.1)',
        padding: '1.2rem',
        borderRadius: '12px',
        borderLeft: '4px solid #60a5fa',
        marginBottom: '1.5rem',
    },
    catchphraseText: {
        fontSize: '1.1rem',
        color: '#fff',
        margin: 0,
        fontStyle: 'italic',
        lineHeight: 1.5,
    },
    divider: {
        height: '1px',
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 20%, rgba(255,255,255,0.1) 80%, rgba(255,255,255,0) 100%)',
        margin: '2rem 0',
    },
    gravityNarrativeBox: {
        marginBottom: '2rem',
    },
    gravityFieldLabel: {
        display: 'block',
        fontSize: '0.85rem',
        color: '#60a5fa',
        marginBottom: '1rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    summaryText: {
        fontSize: '1.1rem',
        lineHeight: 1.8,
        color: '#cbd5e1',
        whiteSpace: 'pre-line',
    },
    typeAnalysisBox: {
        marginTop: '2.5rem',
        padding: '1.5rem',
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
    },
    typeAnalysisItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8rem',
    },
    typeAnalysisLabel: {
        fontSize: '0.85rem',
        color: '#60a5fa',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    typeAnalysisText: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: '#cbd5e1',
        margin: 0,
    },
    subTitle: {
        textAlign: 'center',
        color: '#a78bfa',
        fontSize: '1.1rem',
        marginBottom: '2rem',
        fontStyle: 'italic',
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        padding: '1.2rem 1rem',
        textAlign: 'left',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        color: '#94a3b8',
        fontSize: '0.9rem',
        fontWeight: '600',
        whiteSpace: 'nowrap',
    },
    td: {
        padding: '1.2rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        color: '#cbd5e1',
        fontSize: '0.95rem',
    },
    tdBold: {
        padding: '1.2rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        color: '#60a5fa',
        fontWeight: 'bold',
        fontSize: '1rem',
    },
    tdMission: {
        padding: '1.2rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        color: '#94a3b8',
        fontSize: '0.9rem',
    },
    tdDesc: {
        padding: '1.2rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        color: '#94a3b8',
        fontSize: '0.9rem',
        lineHeight: 1.5,
    },
    axisCardExpanded: {
        backgroundColor: 'rgba(30, 41, 59, 0.4)',
        borderRadius: '24px',
        padding: '2.5rem',
        marginBottom: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    axisHeaderExpanded: {
        display: 'flex',
        alignItems: 'center',
        gap: '1.2rem',
        marginBottom: '2.5rem',
    },
    axisBadgeExpanded: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '1.3rem',
        fontWeight: 'bold',
        color: '#60a5fa',
    },
    axisTitleExpanded: {
        fontSize: '1.6rem',
        fontWeight: '900',
        color: '#fff',
        margin: 0,
    },
    expansionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        marginBottom: '2.5rem',
    },
    expansionItem: {
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        padding: '1.5rem',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    evolutionMapBox: {
        gridColumn: '1 / -1',
        backgroundColor: 'rgba(16, 185, 129, 0.04)',
        padding: '2rem',
        borderRadius: '20px',
        border: '1px solid rgba(16, 185, 129, 0.15)',
    },
    expansionLabel: {
        display: 'block',
        fontSize: '0.85rem',
        color: '#94a3b8',
        marginBottom: '1rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    expansionLabelPrimary: {
        display: 'block',
        fontSize: '0.85rem',
        color: '#a78bfa',
        marginBottom: '1rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    expansionLabelAccent: {
        display: 'block',
        fontSize: '0.85rem',
        color: '#10b981',
        marginBottom: '1rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    expansionText: {
        fontSize: '1.05rem',
        lineHeight: 1.7,
        color: '#e2e8f0',
        margin: 0,
    },
    deepInsightBoxv5: {
        backgroundColor: 'rgba(236, 72, 153, 0.04)',
        padding: '2rem',
        borderRadius: '16px',
        border: '1px solid rgba(236, 72, 153, 0.15)',
        marginTop: '1.5rem',
    },
    deepInsightLabelv5: {
        display: 'block',
        fontSize: '0.9rem',
        color: '#ec4899',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    deepInsightTextv5: {
        fontSize: '1.05rem',
        lineHeight: 1.7,
        color: '#fce7f3',
        margin: 0,
    },
    // New Styles for SEB Integration
    deepAnalysisBox: {
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        padding: '2rem',
        borderRadius: '16px',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        marginTop: '1.5rem',
    },
    deepAnalysisLabel: {
        display: 'block',
        fontSize: '0.9rem',
        color: '#60a5fa',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    deepAnalysisText: {
        fontSize: '1rem',
        lineHeight: 1.7,
        color: '#e2e8f0',
        margin: 0,
    },
    loveInsightBox: {
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        padding: '1.5rem',
        borderRadius: '16px',
        borderLeft: '4px solid #f43f5e',
        marginTop: '1.5rem',
        boxShadow: '0 4px 15px rgba(244, 63, 94, 0.05)',
    },
    loveInsightLabel: {
        fontSize: '1rem', // slightly larger
        color: '#fb7185',
        fontWeight: 'bold',
        marginBottom: '0.8rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    loveInsightText: {
        fontSize: '1.05rem',
        lineHeight: 1.6,
        color: '#fff', // brighter text for contrast in love box
        fontStyle: 'italic',
        margin: 0,
    },

    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
    },
    moduleCard: {
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        padding: '2rem',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    moduleTitle: {
        fontSize: '1.2rem',
        color: '#fff',
        marginBottom: '0.8rem',
        fontWeight: 'bold',
    },
    moduleCode: {
        fontSize: '0.8rem',
        color: '#f43f5e',
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '6px',
        marginBottom: '1.2rem',
        fontWeight: 'bold',
        letterSpacing: '0.5px',
    },
    moduleDesc: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: '#cbd5e1',
    },
    resistanceHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1.2rem',
    },
    resistanceBadge: {
        fontSize: '0.75rem',
        backgroundColor: '#f43f5e',
        color: '#fff',
        padding: '3px 10px',
        borderRadius: '6px',
        fontWeight: 'bold',
        letterSpacing: '1px',
    },
    resistanceTitle: {
        fontSize: '1.2rem',
        color: '#fff',
        margin: 0,
        fontWeight: 'bold',
    },
    resistanceText: {
        fontSize: '1.05rem',
        lineHeight: 1.8,
        color: '#fda4af',
        margin: 0,
    },
    solutionItem: {
        marginBottom: '2rem',
        paddingBottom: '2rem',
        borderBottom: '1px dashed rgba(255,255,255,0.1)',
    },
    solutionTitle: {
        fontSize: '1.2rem',
        color: '#60a5fa',
        marginBottom: '0.8rem',
        fontWeight: 'bold',
    },
    solutionDesc: {
        fontSize: '1.05rem',
        lineHeight: 1.7,
        color: '#e2e8f0',
    },
    expertSummaryBox: {
        backgroundColor: 'rgba(251, 191, 36, 0.06)',
        borderRadius: '24px',
        padding: '2rem',
        border: '1px solid rgba(251, 191, 36, 0.2)',
        marginTop: '2.5rem',
    },
    expertTitle: {
        color: '#fbbf24',
        fontSize: '1.2rem',
        marginBottom: '1.2rem',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    expertText: {
        color: '#fef3c7',
        fontSize: '1.1rem',
        lineHeight: 1.8,
        fontStyle: 'italic',
        textAlign: 'center',
        margin: 0,
    },
    message: {
        textAlign: 'center',
        lineHeight: 1.6,
        marginBottom: '2rem',
        fontSize: '1.1rem',
        color: '#f8fafc',
    },
    button: {
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        color: '#fff',
        border: 'none',
        padding: '1.2rem 2.5rem',
        borderRadius: '16px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
        transition: 'all 0.3s ease',
    },
    errorCard: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        borderRadius: '24px',
        padding: '3.5rem 2.5rem',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
        textAlign: 'center',
    },
    buttonGroup: {
        display: 'flex',
        gap: '1.2rem',
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: '2.5rem',
    },
    textButton: {
        background: 'transparent',
        border: 'none',
        color: '#94a3b8',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: '0.95rem',
    },
    loadingBox: {
        textAlign: 'center',
        padding: '4rem',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid rgba(255, 255, 255, 0.1)',
        borderLeftColor: '#3b82f6',
        borderRadius: '50%',
        margin: '0 auto',
    }
};

export default InsideReport;
